// Messaging preferences: who-can-DM gate, blocks, mutes, reports.
// Runtime tuples + derived types live together here (see feedback.ts);
// the .runtime.js/.runtime.d.ts shims re-export the constants for
// nodenext ESM consumers.

// `candidate` covers everyone who is not sending on behalf of a company:
// ordinary job seekers. Without it a seeker could not be classified at all, so
// the guard failed closed and seeker-to-seeker DMs were impossible.
export const SENDER_ROLES = [
  'candidate',
  'company',
  'recruiter',
  'founder',
  'agency',
  'freelancer',
] as const;

export type SenderRole = (typeof SENDER_ROLES)[number];

export const WORKSPACE_SENDER_ROLES = [
  'master', // master switch: anyone can DM the workspace
  'candidate_who_applied', // candidates who already submitted an application
  'all_candidates', // cold inbound candidate DMs
  'freelancers', // project-based contractors
  'agency', // staffing partners pitching candidates
] as const;

export type WorkspaceSenderRole = (typeof WORKSPACE_SENDER_ROLES)[number];

export const REPORT_CATEGORIES = [
  'spam',
  'harassment',
  'impersonation',
  'inappropriate_content',
  'other',
] as const;

export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const USER_REPORT_STATUSES = [
  'pending',
  'reviewing',
  'actioned',
  'dismissed',
] as const;

export type UserReportStatus = (typeof USER_REPORT_STATUSES)[number];

/** Reporter-safe projection. Internal notes and reviewer identity stay private. */
export interface UserReportSummary {
  id: string;
  reportedUserId: string | null;
  reportedUserName: string;
  category: ReportCategory;
  status: UserReportStatus;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
}

export interface UserReportSubmissionResult {
  reported: true;
  report: UserReportSummary;
}

// Defaults: server and client must agree, so they live in @tabbio/types
// rather than being duplicated in the API guard and the prefs screen.
//
// Personal DMs are OPEN by default: anyone may message anyone, and a user who
// wants a narrower inbox opts into that from messaging preferences. The old
// default was company/recruiter/founder only, which combined with the missing
// `candidate` role meant an ordinary job seeker could not start a conversation
// with anyone at all. Blocks and reports are unaffected by this.
export const DEFAULT_SEEKER_ALLOWED: SenderRole[] = [...SENDER_ROLES];

export const DEFAULT_EMPLOYER_ALLOWED: WorkspaceSenderRole[] = [
  'candidate_who_applied',
  'agency',
];

export interface MessagingPreferenceSummary {
  userId: string;
  allowedSenderRoles: SenderRole[];
  allowedWorkspaceSenderRoles: WorkspaceSenderRole[];
  autoBlockSpam: boolean;
  updatedAt: string;
}

// Gate result returned by createConversation/sendMessage.
export type PreferenceGateResult =
  | { allowed: true }
  | {
      allowed: false;
      code: 'RESTRICTED_BY_PREFS';
      // Roles the recipient DOES accept: rendered on PreferenceGatedScreen.
      allowedRoles: SenderRole[] | WorkspaceSenderRole[];
      recipientId: string;
      recipientName: string;
      // If the recipient is hiring, surface a non-DM path instead of just blocking.
      jobApplyHint?: { jobId: string; jobTitle: string; companyName: string };
    }
  | {
      allowed: false;
      code: 'BLOCKED';
      // Generic: never reveal who blocked whom beyond the implicit hint.
      recipientId: string;
    };

export interface BlockedUserSummary {
  id: string;
  blockedUserId: string;
  blockedUserName: string;
  blockedUserImage?: string | null;
  createdAt: string;
  reason?: string | null;
}

export type MuteDuration = '1h' | '8h' | '1w' | 'indefinite';

export interface ConversationMuteSummary {
  conversationId: string;
  mutedAt: string;
  until: string | null;
}
